"use client";
import { Avatar } from "@base-ui/react/avatar";
import { Select } from "@base-ui/react/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { BulletList } from "@tiptap/extension-bullet-list";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  ChevronDown,
  ImagePlus,
  Italic,
  List,
  Strikethrough,
  UnderlineIcon,
  X,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { uploadFile } from "@/actions/file.action";
import { createTopic, deleteTopic, updateTopic } from "@/actions/forum.action";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getForumCategories } from "./actions";

const MAX_IMAGES = 5;

const TopicSchema = z.object({
  categoryId: z.string().min(1, { error: "Kategori seçiniz" }),
  imageUrls: z.array(z.string()).optional(),
  subCategoryId: z.string().optional(),
  text: z.string({ error: "Açıklama zorunludur" }).min(1, { error: "Açıklama zorunludur" }),
  title: z.string({ error: "Başlık zorunludur" }).min(1, { error: "Başlık zorunludur" }),
});
export type TopicForm = z.infer<typeof TopicSchema>;

interface Props {
  user?: { name: string; surname: string; imageUrl?: string };
  onSuccess?: () => void;
  mode?: "create" | "edit";
  initialData?: {
    id: string;
    categoryId: string;
    subCategoryId?: string;
    title: string;
    text: string;
    imageUrls: string[];
  };
}

const TopicForm = ({ user, onSuccess, mode = "create", initialData }: Props) => {
  const router = useRouter();
  const [images, setImages] = useState<File[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>(
    initialData?.imageUrls ?? [],
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<TopicForm>({
    defaultValues: {
      categoryId: initialData?.categoryId ?? "",
      imageUrls: [],
      subCategoryId: initialData?.subCategoryId ?? "",
      text: initialData?.text ?? "",
      title: initialData?.title ?? "",
    },
    mode: "onChange",
    resolver: zodResolver(TopicSchema),
  });

  const {
    formState: { isValid, isSubmitting, errors },
  } = form;
  const watchedCategoryId = form.watch("categoryId");

  const { data: categories = [] } = useQuery({
    queryFn: () => getForumCategories(),
    queryKey: ["forum-categories"],
  });

  const { data: subCategories = [] } = useQuery({
    enabled: !!watchedCategoryId,
    queryFn: () => getForumCategories(watchedCategoryId),
    queryKey: ["forum-subcategories", watchedCategoryId],
  });

  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (name === "categoryId") {
        form.setValue("subCategoryId", "", { shouldValidate: false });
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const editor = useEditor({
    content: initialData?.text,
    editorProps: {
      attributes: {
        class:
          "outline-none min-h-[120px] text-sm text-neutral-700 leading-relaxed px-3 py-2",
      },
    },
    extensions: [
      StarterKit.configure({ bulletList: false }),
      BulletList,
      Underline,
    ],
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      form.setValue("text", editor.isEmpty ? "" : editor.getHTML(), { shouldValidate: true });
    },
  });

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      return [...prev, ...files.slice(0, remaining)];
    });
    e.target.value = "";
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDelete = async () => {
    if (!initialData) {
      return;
    }
    const res = await deleteTopic(initialData.id);
    if (res.isSuccess) {
      router.push("/forum");
    }
  };

  const handleSubmit = async (data: TopicForm) => {
    let newlyUploadedUrls: string[] = [];
    if (images.length > 0) {
      const results = await Promise.all(
        images.map((f) => uploadFile(f, "forum/topics")),
      );
      newlyUploadedUrls = results.flatMap((r) => (r.isSuccess ? [r.result.fileUrl] : []));
    }
    const allImageUrls = [...existingImageUrls, ...newlyUploadedUrls];

    if (mode === "edit" && initialData) {
      const res = await updateTopic(initialData.id, {
        categoryId: data.categoryId,
        imageUrls: allImageUrls.length > 0 ? allImageUrls : undefined,
        subCategoryId: data.subCategoryId,
        text: data.text,
        title: data.title,
      });
      if (res.isSuccess) {
        router.push(`/forum/topic/${initialData.id}`);
        onSuccess?.();
      }
    } else {
      const res = await createTopic({
        categoryId: data.categoryId,
        imageUrls: allImageUrls.length > 0 ? allImageUrls : undefined,
        subCategoryId: data.subCategoryId,
        text: data.text,
        title: data.title,
      });
      if (res.isSuccess) {
        router.push(`/forum/topic/${res.result.id}`);
        onSuccess?.();
      }
    }
  };

  const submitLabel = mode === "edit" ? "Kaydet" : "Gönder";

  return (
    <form
      className="flex flex-col gap-4 pt-4"
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      {/* User header */}
      {user && (
        <div className="flex items-center gap-3">
          <Avatar.Root className="inline-flex size-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 shrink-0">
            {user.imageUrl && (
              <Avatar.Image
                alt={user.name}
                className="size-full object-cover"
                src={user.imageUrl}
              />
            )}
            <Avatar.Fallback className="flex size-full items-center justify-center text-sm font-medium text-gray-700">
              {user.name[0]}
              {user.surname[0]}
            </Avatar.Fallback>
          </Avatar.Root>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">
              {user.name} {user.surname}
            </span>
            <span className="text-xs text-neutral-500">Üye</span>
          </div>
        </div>
      )}

      {/* Category + SubCategory */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-semibold text-gray-900"
            htmlFor="topic-category"
          >
            Kategori
          </label>
          <Controller
            control={form.control}
            name="categoryId"
            render={({ field, fieldState }) => (
              <>
                <Select.Root onValueChange={field.onChange} value={field.value}>
                  <Select.Trigger
                    className="flex w-full h-10 items-center justify-between rounded-lg border border-neutral-200 px-3 text-sm bg-white cursor-pointer focus:outline-none focus:border-brand-blue-300"
                    id="topic-category"
                  >
                    <span
                      className={cn(
                        "truncate",
                        field.value ? "text-gray-900" : "text-neutral-400",
                      )}
                    >
                      {field.value
                        ? (categories.find((c) => c.id === field.value)?.name ??
                          "Kategori Seç")
                        : "Kategori Seç"}
                    </span>
                    <ChevronDown className="size-4 shrink-0 stroke-brand-blue-500 fill-none" />
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Positioner sideOffset={4}>
                      <Select.Popup className="rounded-lg border border-neutral-200 bg-white shadow-lg py-1 z-50 max-h-60 overflow-y-auto">
                        <Select.List>
                          {categories.map((cat) => (
                            <Select.Item
                              className="flex items-center px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-neutral-50 data-[highlighted]:bg-neutral-50 outline-none"
                              key={cat.id}
                              value={cat.id}
                            >
                              <Select.ItemText>{cat.name}</Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.List>
                      </Select.Popup>
                    </Select.Positioner>
                  </Select.Portal>
                </Select.Root>
                {fieldState.error && (
                  <p className="text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-semibold text-gray-900"
            htmlFor="topic-subcategory"
          >
            Alt Kategori
          </label>
          <Controller
            control={form.control}
            name="subCategoryId"
            render={({ field }) => (
              <Select.Root
                disabled={!watchedCategoryId || subCategories.length === 0}
                onValueChange={field.onChange}
                value={field.value}
              >
                <Select.Trigger
                  className="flex w-full h-10 items-center justify-between rounded-lg border border-neutral-200 px-3 text-sm bg-white cursor-pointer focus:outline-none focus:border-brand-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  id="topic-subcategory"
                >
                  <span
                    className={cn(
                      "truncate",
                      field.value ? "text-gray-900" : "text-neutral-400",
                    )}
                  >
                    {field.value
                      ? (subCategories.find((c) => c.id === field.value)
                          ?.name ?? "Alt Kategori Seç")
                      : "Alt Kategori Seç"}
                  </span>
                  <ChevronDown className="size-4 shrink-0 stroke-brand-blue-500 fill-none" />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Positioner sideOffset={4}>
                    <Select.Popup className="rounded-lg border border-neutral-200 bg-white shadow-lg py-1 z-50 max-h-60 overflow-y-auto">
                      <Select.List>
                        {subCategories.map((cat) => (
                          <Select.Item
                            className="flex items-center px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-neutral-50 data-[highlighted]:bg-neutral-50 outline-none"
                            key={cat.id}
                            value={cat.id}
                          >
                            <Select.ItemText>{cat.name}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Popup>
                  </Select.Positioner>
                </Select.Portal>
              </Select.Root>
            )}
          />
        </div>
      </div>

      {/* Title */}
      <div className="flex flex-col gap-1">
        <label
          className="text-sm font-semibold text-gray-900"
          htmlFor="topic-title"
        >
          Konu Başlığı
        </label>
        <Controller
          control={form.control}
          name="title"
          render={({ field, fieldState }) => (
            <>
              <input
                {...field}
                className="w-full h-10 rounded-lg border border-neutral-200 px-3 text-sm text-gray-900 placeholder:text-neutral-400 focus:outline-none focus:border-brand-blue-300"
                id="topic-title"
                placeholder="Başlık Ekle"
                type="text"
              />
              {fieldState.error && (
                <p className="text-sm text-red-600">{fieldState.error.message}</p>
              )}
            </>
          )}
        />
      </div>

      {/* Description editor */}
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-gray-900">Açıklama</span>
        <div className="rounded-lg border border-neutral-200 focus-within:border-brand-blue-300 transition-colors">
          <EditorContent editor={editor} />

          {(existingImageUrls.length > 0 || images.length > 0) && (
            <div className="flex flex-wrap gap-2 px-3 pb-2">
              {existingImageUrls.map((url, i) => (
                <div className="relative size-16 shrink-0" key={url}>
                  <Image
                    alt={`görsel-${i + 1}`}
                    className="rounded-md object-cover border border-neutral-200"
                    fill={true}
                    src={url}
                  />
                  <button
                    className="absolute -top-1.5 -right-1.5 size-4 flex items-center justify-center rounded-full bg-neutral-700 text-white hover:bg-neutral-900"
                    onClick={() => setExistingImageUrls((prev) => prev.filter((_, j) => j !== i))}
                    type="button"
                  >
                    <X className="size-2.5" />
                  </button>
                </div>
              ))}
              {images.map((file, i) => (
                <div
                  className="relative size-16 shrink-0"
                  key={`${file.name}-${file.lastModified}`}
                >
                  <Image
                    alt={file.name}
                    className="rounded-md object-cover border border-neutral-200"
                    fill={true}
                    src={URL.createObjectURL(file)}
                  />
                  <button
                    className="absolute -top-1.5 -right-1.5 size-4 flex items-center justify-center rounded-full bg-neutral-700 text-white hover:bg-neutral-900"
                    onClick={() => removeImage(i)}
                    type="button"
                  >
                    <X className="size-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center border-t border-neutral-100 px-2 py-1.5 gap-0.5">
            <input
              accept="image/*"
              className="hidden"
              multiple={true}
              onChange={handleImageSelect}
              ref={fileInputRef}
              type="file"
            />
            <button
              className={cn(
                "size-7 flex items-center justify-center rounded hover:bg-neutral-100 relative",
                images.length > 0 && "text-brand-blue-500",
              )}
              disabled={images.length >= MAX_IMAGES}
              onClick={() => fileInputRef.current?.click()}
              title={
                images.length >= MAX_IMAGES
                  ? `En fazla ${MAX_IMAGES} görsel eklenebilir`
                  : "Görsel ekle"
              }
              type="button"
            >
              <ImagePlus className="size-4 text-neutral-600" />
              {images.length > 0 && (
                <span className="absolute -top-1 -right-1 size-3.5 flex items-center justify-center rounded-full bg-brand-blue-500 text-white text-[9px] leading-none font-medium">
                  {images.length}
                </span>
              )}
            </button>

            <div className="w-px h-4 bg-neutral-200 mx-0.5" />

            <button
              className={cn(
                "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
                editor?.isActive("bulletList") && "bg-neutral-100",
              )}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              type="button"
            >
              <List className="size-4 text-neutral-600" />
            </button>

            <button
              className={cn(
                "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
                editor?.isActive("bold") && "bg-neutral-100",
              )}
              onClick={() => editor?.chain().focus().toggleBold().run()}
              type="button"
            >
              <Bold className="size-4 text-neutral-600" />
            </button>

            <button
              className={cn(
                "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
                editor?.isActive("italic") && "bg-neutral-100",
              )}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              type="button"
            >
              <Italic className="size-4 text-neutral-600" />
            </button>

            <button
              className={cn(
                "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
                editor?.isActive("underline") && "bg-neutral-100",
              )}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              type="button"
            >
              <UnderlineIcon className="size-4 text-neutral-600" />
            </button>

            <button
              className={cn(
                "size-7 flex items-center justify-center rounded hover:bg-neutral-100",
                editor?.isActive("strike") && "bg-neutral-100",
              )}
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              type="button"
            >
              <Strikethrough className="size-4 text-neutral-600" />
            </button>
          </div>
        </div>
        {errors.text && (
          <p className="text-sm text-red-800">{errors.text.message}</p>
        )}
      </div>

      <Button
        className="w-full rounded-full"
        disabled={!isValid || isSubmitting}
        type="submit"
      >
        {isSubmitting ? "Kaydediliyor..." : submitLabel}
      </Button>
      {mode === "edit" && (
        <Button
          className="w-full rounded-full"
          onClick={handleDelete}
          type="button"
          variant="ghost"
        >
          Konuyu Sil
        </Button>
      )}
    </form>
  );
};

export default TopicForm;
